// Keys whose values must never reach Sentry: OAuth authorization codes and
// user-entered exclusion lists are sensitive (auth material, private PII).
// Two surfaces feed Sentry from the request layer:
//   - `services/request.js` ships `customOptions.data` (the request body) into
//     `trackException` on HTTP failures.
//   - `redux/store.js` ships every RTK Query mutation action through the
//     Sentry Redux enhancer, where the mutation args live under
//     `meta.arg.originalArgs` (not `payload`).
// Both surfaces share this list so a new mutation contributor only has to
// extend it in one place.
export const SENSITIVE_BODY_KEYS = ['code', 'excluded_emails', 'excluded_domains', 'excludedEmails', 'excludedDomains'];

// User-facing PII that should never reach Sentry breadcrumbs or context.
// Lives here (not in `redux/store.js`) so every telemetry surface — Redux
// enhancer, request error tracker, future contributors — composes the same
// drop list with `redactSensitiveKeys`. Their key sets are intentionally
// disjoint: this one drops broad identity fields, `SENSITIVE_BODY_KEYS`
// drops auth-material keys.
export const RESTRICTED_KEYS = ['user', 'jobfunctions', 'firstName', 'lastName', 'email', 'currentLocation'];

export const removeKeys = (obj, keys = RESTRICTED_KEYS) => {
  if (obj !== Object(obj)) return obj;
  if (Array.isArray(obj)) return obj.map((item) => removeKeys(item, keys));
  return Object.keys(obj)
    .filter((k) => !keys.includes(k))
    .reduce((acc, x) => Object.assign(acc, { [x]: removeKeys(obj[x], keys) }), {});
};

// Composed scrubber: drops PII keys AND redacts auth-material keys. Use this
// for every Sentry surface that snapshots user-controlled data (mutation
// args, RTK Query state slice, request bodies on error).
//
// Pre-serialized JSON string bodies must also pass through both scrubbers,
// not just `redactSensitiveKeys`. `removeKeys` no-ops on non-objects, so a
// JSON string carrying `{"email":"…"}` would survive a naive composition.
// The string branch delegates to `tryRedactJsonString` so the parse →
// compose → re-serialize sequence lives in exactly one place — a future
// edit to either path lands in both surfaces and the two can't drift.
// Non-JSON strings (form data, signed tokens, plain text) are left alone.
export const scrubForSentry = (value) => {
  if (typeof value === 'string') {
    // eslint-disable-next-line no-use-before-define
    return tryRedactJsonString(value, SENSITIVE_BODY_KEYS);
  }
  // eslint-disable-next-line no-use-before-define
  return redactSensitiveKeys(removeKeys(value, RESTRICTED_KEYS));
};

const REDACTED = '[REDACTED]';

// `services/request.js` lets callers pass `customOptions.data` as a
// pre-serialized JSON string (the `transformRequest` GSUP-48 note documents
// this), and on error that exact string lands in Sentry context. Detect the
// JSON-string shape, redact the parsed body, and re-serialize so a string
// payload is scrubbed as thoroughly as an object payload. Non-JSON strings
// are left alone — they could be anything (form data, plain text, signed
// tokens) and over-redacting them would risk corrupting the breadcrumb.
//
// Nested JSON strings (e.g. `{"payload":"{\"email\":\"…\"}"}`) reach this
// path during the recursive walk inside `redactSensitiveKeys` because the
// inner value is itself a string. Both scrubbers must run on the parsed
// body — `removeKeys(RESTRICTED_KEYS)` is what strips PII like `email` /
// `firstName`; running only `redactSensitiveKeys` would leave them
// untouched and ship them to Sentry verbatim.
const tryRedactJsonString = (value, keys) => {
  if (value.length === 0) return value;
  // Trim first so a body emitted via `JSON.stringify(payload, null, 2)` or
  // wrapped with stray whitespace is still detected as JSON-shaped. Without
  // this, a leading space would short-circuit and ship the OAuth `code`
  // verbatim.
  const trimmed = value.replace(/^\s+/, '');
  const head = trimmed[0];
  if (head !== '{' && head !== '[') return value;
  try {
    const parsed = JSON.parse(trimmed);
    // eslint-disable-next-line no-use-before-define
    return JSON.stringify(redactSensitiveKeys(removeKeys(parsed, RESTRICTED_KEYS), keys));
  } catch {
    return value;
  }
};

export const redactSensitiveKeys = (value, keys = SENSITIVE_BODY_KEYS) => {
  if (value == null) return value;
  if (typeof value === 'string') return tryRedactJsonString(value, keys);
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => redactSensitiveKeys(item, keys));
  return Object.keys(value).reduce((acc, key) => {
    acc[key] = keys.includes(key) ? REDACTED : redactSensitiveKeys(value[key], keys);
    return acc;
  }, {});
};
